"use client";

import { useEffect, useState } from "react";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";
import {
    CANVAS_W,
    CANVAS_H,
    type SnapGuide,
} from "@/features/lomdaEditorV2/lib/elements";
import {
    useIsTransforming,
    useLiveGuides,
    useUiActions,
} from "@/features/lomdaEditorV2/store";

const pct = (n: number, total: number) => `${(n / total) * 100}%`;

/**
 * Smart-alignment overlay shown during any move/resize/rotate gesture: fuchsia
 * lines where an edge or center lines up with a sibling, the canvas, or the
 * content safe-area. Positions arrive in canvas-px (1920×1080) and are converted
 * to % of the scene container. A hint, shown for the whole drag, reminds that
 * Alt frees it — and pressing Alt clears the guides instantly and hides the hint.
 */
export function SnapGuides() {
    const dragging = useIsTransforming();
    const guides = useLiveGuides();
    const { clearLiveGuides } = useUiActions();
    const { locale } = useLocale();
    const [altHeld, setAltHeld] = useState(false);

    useEffect(() => {
        if (!dragging) {
            setAltHeld(false);
            return;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Alt" || e.repeat) return;
            setAltHeld(true);
            clearLiveGuides();
        };
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.key === "Alt") setAltHeld(false);
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, [dragging, clearLiveGuides]);

    if (!dragging) return null;

    return (
        <div className="pointer-events-none absolute inset-0 z-[9999] overflow-hidden">
            {guides.map((g, i) => (
                <AlignmentLine key={i} g={g} />
            ))}
            {!altHeld && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-neutral-900/80 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm">
                    {t("lomda-editor.canvas.snap-hint", locale)}
                </div>
            )}
        </div>
    );
}

function AlignmentLine({ g }: { g: SnapGuide }) {
    return g.axis === "x" ? (
        <div
            className="absolute w-px bg-fuchsia-500"
            style={{
                left: pct(g.pos, CANVAS_W),
                top: pct(g.start, CANVAS_H),
                height: pct(g.end - g.start, CANVAS_H),
            }}
        />
    ) : (
        <div
            className="absolute h-px bg-fuchsia-500"
            style={{
                top: pct(g.pos, CANVAS_H),
                left: pct(g.start, CANVAS_W),
                width: pct(g.end - g.start, CANVAS_W),
            }}
        />
    );
}
